import { useCallback, useEffect, useMemo } from 'react'
import { useMutation, useSuspenseQuery } from '@apollo/client/react'
import {
  AdminAPI,
  getFragment,
  GraphQL,
  makeFragmentData,
  SharedAPI
} from '@guestgoodie/graphql'

export function useMemberships() {
  const { data } = useSuspenseQuery(AdminAPI.membership.GET, {
    variables: {
      withUser: true
    }
  })

  const members = useMemo(
    () =>
      data.membershipsForCurrentOrg.map(membership => {
        const member = getFragment(
          SharedAPI.membership.MEMBERSHIP_FRAGMENT,
          membership
        )

        return {
          ...member,
          user: getFragment(SharedAPI.user.USER_FRAGMENT, member.user!)
        }
      }),
    [data]
  )

  return members
}

export function useMembershipsWithSubscription() {
  const { data, subscribeToMore } = useSuspenseQuery(AdminAPI.membership.GET, {
    variables: {
      withUser: true
    }
  })

  const [updateMember] = useMutation(AdminAPI.membership.UPDATE)
  const [deleteMember] = useMutation(AdminAPI.membership.DELETE)

  const members = useMemo(
    () =>
      data.membershipsForCurrentOrg.map(membership => {
        const member = getFragment(
          SharedAPI.membership.MEMBERSHIP_FRAGMENT,
          membership
        )

        return {
          ...member,
          user: getFragment(SharedAPI.user.USER_FRAGMENT, member.user!)
        }
      }),
    [data]
  )

  const onUpdate = useCallback(
    (id: number, newRole: GraphQL.Role) => {
      const currentMember = members.find(member => member.id === id)

      if (!currentMember) return

      updateMember({
        variables: {
          input: {
            id,
            role: newRole
          }
        },
        optimisticResponse: {
          updateMembership: makeFragmentData(
            {
              ...currentMember,
              role: newRole
            },
            SharedAPI.membership.MEMBERSHIP_FRAGMENT
          )
        }
      })
    },
    [members, updateMember]
  )

  const onDelete = useCallback(
    (id: number) => {
      const currentMember = members.find(member => member.id === id)

      if (!currentMember) return

      deleteMember({
        variables: {
          id
        },
        optimisticResponse: {
          deleteMembership: currentMember
        },
        update(cache, { data }) {
          if (!data) return

          const normalizedId = cache.identify(data.deleteMembership)

          cache.modify<GraphQL.MembershipsForCurrentOrgQuery>({
            id: 'ROOT_QUERY',
            fields: {
              membershipsForCurrentOrg(existingMembers = [], { toReference }) {
                return existingMembers.filter(
                  memberRef => toReference(memberRef)?.__ref !== normalizedId
                )
              }
            }
          })

          cache.evict({ id: normalizedId })
          cache.gc()
        }
      })
    },
    [members, deleteMember]
  )

  useEffect(() => {
    subscribeToMore({
      document: AdminAPI.membership.CHANGED,
      updateQuery: (
        prev,
        { subscriptionData }
      ): GraphQL.MembershipsForCurrentOrgQuery => {
        if (!subscriptionData.data) {
          return prev as GraphQL.MembershipsForCurrentOrgQuery
        }

        const { membership, event } = subscriptionData.data.membershipsChanged

        const previousData = prev as GraphQL.MembershipsForCurrentOrgQuery
        const prevItems = previousData.membershipsForCurrentOrg ?? []

        const membershipFrag = getFragment(
          SharedAPI.membership.MEMBERSHIP_FRAGMENT,
          membership
        )

        if (event === GraphQL.SubscriptionEvent.Create) {
          return {
            ...previousData,
            membershipsForCurrentOrg: [...prevItems, membership]
          }
        }

        if (event === GraphQL.SubscriptionEvent.Update) {
          return {
            ...previousData,
            membershipsForCurrentOrg: prevItems.map(item => {
              const itemFrag = getFragment(
                SharedAPI.membership.MEMBERSHIP_FRAGMENT,
                item
              )
              return itemFrag.id === membershipFrag.id ? membership : item
            })
          }
        }

        if (event === GraphQL.SubscriptionEvent.Delete) {
          return {
            ...previousData,
            membershipsForCurrentOrg: prevItems.filter(item => {
              const itemFrag = getFragment(
                SharedAPI.membership.MEMBERSHIP_FRAGMENT,
                item
              )
              return itemFrag.id !== membershipFrag.id
            })
          }
        }

        return previousData
      }
    })
  }, [subscribeToMore])

  return {
    members,
    onUpdate,
    onDelete
  }
}
